import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/auth';
import Axios from 'axios';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import setPath from 'lodash/set';
const _ = require('lodash');

const callSafe = (f) => {
  return function (...args) {
    return new Promise(async (resolve, reject) => {
      // console.log(`DEBUG: common manager callSafe ${f.name} args ${JSON.stringify(args)}`);
      const params = args[0] || {};
      const {
        rootnode = '',
        id = '',
        data = null, // default return data or data to save/update
        rejectOnError = true, // reject by default
      } = params;
  
      try {
        if (isEmpty(rootnode)) {
          throw new Error(`The given rootnode is empty`);
        }
        if (isEmpty(id)) {
          throw new Error(`The given '${rootnode}' id is empty`);
        }
        
        const result = await f.call(this, ...args); // call org function
        return resolve( result );
      } catch (err) {
        console.error(
          `ERROR: common manager ${f.name} '${JSON.stringify(params)}' errored`,
          err.message || 'unknown error'
        );
        if (rejectOnError) {
          return reject(err); // by default
        }
        resolve(data);
      }
    });
  };
};
const takeParams = (params = {}) => {
  const { rootnode = '', id = '', propPath = '', data = null, existsOnly = false } = params || {};
  const refNode = isEmpty(propPath) ? `${rootnode}/${id}` : `${rootnode}/${id}/${propPath}`;
  return { refNode, data, existsOnly };
};
const fbGet = (params = {}) => {
  const { refNode, data, existsOnly = false } = takeParams(params);
  return new Promise(async (resolve, reject) => {
    try {
      const snapshot = await firebase.database().ref(refNode).once('value');
      if (existsOnly) {
        return resolve(snapshot.exists());
      }
      if (!snapshot.exists()) {
        return resolve(data);
      }
      return resolve(snapshot.val());
    } catch (err) {
      reject(err)
    }
  });
};
const fbSet = async (params = {}) => {
  const { refNode, data } = takeParams(params);
  return firebase.database().ref(refNode).set(data);
};
const fbUpdate = async (params = {}) => {
  const { refNode, data } = takeParams(params);
  return firebase.database().ref(refNode).update(data);
};
const fbRemove = async (params = {}) => {
  const { refNode } = takeParams(params);
  return firebase.database().ref(refNode).remove();
};
const fbFirstByChildSafe = (params = {}) => {
  const {
    rootnode = '', propPath = '', 
    childName = '', childValue = '',
    callName = '',
  } = params || {};
  const refNode = isEmpty(propPath) ? `${rootnode}` : `${rootnode}/${propPath}`;
  return new Promise(async resolve => {
    const emptyValue = {};
    try {
      if (isEmpty(rootnode)) {
        throw new Error(`The given rootnode is empty`);
      }
      if (isEmpty(childValue) || isEmpty(childName)) {
        throw new Error(`Unable to lookup '${refNode}' with empty ${childName || 'child'}`);
      }

      const snapshot = await firebase
          .database()
          .ref(refNode)
          .orderByChild(childName)
          .equalTo(childValue)
          .once('value');

      if (!snapshot.exists()) {
        return resolve(emptyValue);
      }
  
      const items = snapshot.val() || {};
      if (isEmpty(items)) {
        return resolve(emptyValue);
      }

      const itemIds = Object.keys(items);
      const id = itemIds[0];
      const item = items[id];
      if (itemIds.length > 1) {
        console.warn(
          `WARNING: fbFirstByChild ${callName} ${childName} ${childValue}`,
          `found ${itemIds.length} ${refNode} ${JSON.stringify(itemIds)} - used first ${id}`
        );
      }

      return resolve({ id, item });
    } catch (err) {
      console.error(
        `ERROR: fbFirstByChild ${callName} ${childName} ${childValue} errored -`,
        err.message
      );
    }
    return resolve(emptyValue);
  });
};

// commonManager is not exported
const commonManager = rootnode => {
  return {
    getByCode: (code = '') => callSafe(fbGet)({ rootnode, id: code, rejectOnError: false, call: 'getByCode' }),
    getById: (id = '') => callSafe(fbGet)({ rootnode, id, rejectOnError: false, call: 'getById' }),

    getProp: (id, propPath, defaultValue = null) =>
      callSafe(fbGet)({ rootnode, id, propPath, data: defaultValue, call: 'getProp' }),
    getProps: async (id, propPaths = []) => {
      // USAGE: await getProps(id, [
      //   'path1', // defaultValue1 === null
      //   ['path2', 'value2'], // defaultValue2 === 'value2'
      //   ['path3/subPath3', false], // defaultValue3 === false
      // ]);
      const resultValues = await Promise.all(
        propPaths.map(item => {
          const [propPath, data = null] = isArray(item) ? item : [item, null];
          return callSafe(fbGet)({ rootnode, id, propPath, data, call: 'getProps' });
        })
      );
      const resultObj = propPaths.reduce((items, item, index) => {
        const [propPath] = isArray(item) ? item : [item];
        return setPath(items, propPath.replace(/\//g, '.'), resultValues[index]);
      }, { id });
      return resultObj;
    },

    saveProp: (id, propPath, data) => callSafe(fbSet)({ rootnode, id, propPath, data, call: 'saveProp' }),
    updateProp: (id, propPath, data) => callSafe(fbUpdate)({ rootnode, id, propPath, data, call: 'updateProp' }),
    removeProp: (id, propPath) => callSafe(fbRemove)({ rootnode, id, propPath, call: 'removeProp' }),

    save: (id, data) => callSafe(fbSet)({ rootnode, id, data, call: 'save' }),
    update: (id, data) => callSafe(fbUpdate)({ rootnode, id, data, call: 'update' }),
    remove: id => callSafe(fbRemove)({ rootnode, id, call: 'remove' }),

    exists: id => callSafe(fbGet)({ rootnode, id, existsOnly: true, call: 'exists' }),
  };
};

const authManager = {
  getCurrentUser: () => firebase.auth().currentUser,
  getUserId: () => firebase.auth().currentUser.uid,
  signOut: () => firebase.auth().signOut(),
  fetchSignInMethodsForEmail: email => firebase.auth().fetchSignInMethodsForEmail(email),
  onAuthStateChanged: observer => {
    firebase.auth().onAuthStateChanged(observer);
  },
  getEmailCredential: (email, password) => {
    return firebase.auth.EmailAuthProvider.credential(email, password);
  },
  checkPassword: (email, password) =>
    new Promise((resolve, reject) => {
      const credential = firebase.auth.EmailAuthProvider.credential(email, password);
      firebase
        .auth()
        .currentUser.reauthenticateWithCredential(credential)
        .then(resolve)
        .catch(reject);
    }),
  signInWithEmailAndPassword: (email, password) =>
    firebase.auth().signInWithEmailAndPassword(email, password),
  signInWithFacebookPopup: () => {
    const facebookProvider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(facebookProvider);
  },
};

const basicManager = {
  update: itemsToUpdate => firebase.database().ref().update(itemsToUpdate),
};

const couponsManager = {
  ...commonManager('coupons'),
  getByAffiliate: (stripeUserId = '') => {
    return new Promise(async resolve => {
      const { id = null } = await fbFirstByChildSafe({
        rootnode: 'coupons', propPath: '', 
        childName: 'affiliate', childValue: stripeUserId,
        callName: 'couponsManager.getByAffiliate',
      });
      return resolve(id);
    });
  },
};

const courseManager = {
  ...commonManager('courses'),
  getAll: () => {
    return new Promise(async resolve => {
      try {
        const snapshot = await firebase.database().ref(`courses`).once('value');
        if (!snapshot.exists()) {
          throw new Error(`The courses does not exists`);
        }

        resolve(snapshot.val());
      } catch (err) {
        console.error(`ERROR: Course manager - get all courses errored`, err.message);
        resolve(null);
      }
    });
  },
  pushReview: (courseId, review) => {
    if (isEmpty(courseId)) {
      return console.error(`ERROR: Course manager - pushReview errored course id is empty`);
    }
    firebase.database().ref(`courses/${courseId}`).child('reviews').push(review);
  },
};

const episodeManager = {
  ...commonManager('episodes'),
};

const messageManager = {
  ...commonManager('messages'),
};

const publisherManager = {
  ...commonManager('publishers'),
};

const soundcastManager = {
  ...commonManager('soundcasts'),
  
  removeInvitedUserByEmailBulk: (id, emails) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (isEmpty(id)) {
          throw new Error(`The given soundcast id is empty`);
        }
        if (isEmpty(emails)) {
          throw new Error(`The given emails is empty`);
        }

        const nodeUpdates = emails.reduce((accum, email) => {
          if (!isEmpty(email)) {
            const dotEmail = email.replace(/\./g, '(dot)').toLowerCase().trim();
            accum[`soundcasts/${id}/invited/${dotEmail}`] = null;
            accum[`invitations/${dotEmail}/${id}`] = null;
          }
          return accum;
        }, {});

        await basicManager.update(nodeUpdates);

        resolve(true);
      } catch (err) {
        console.error(
          `ERROR: Soundcast manager - removeInvitedUserByEmailBulk errored`,
          err.message,
        );
        reject(err);
      }
    });
  },

  getSubscribedObj: async (soundcastId, subscribed = null) => {
    const [ oldSubscribers, extSubscribers] = await Promise.all([
      isNull(subscribed)
        ? soundcastManager.getProp(soundcastId, 'subscribed') // deprecated
        : subscribed,
      soundcastExtManager.getProp(soundcastId, 'subscribed'),
    ]);
    const subscribers = {
      ...(oldSubscribers || {}),
      ...(extSubscribers || {}),
    };
    return subscribers;
  },
};

const soundcastExtManager = {
  ...commonManager('soundcastExts'),
};

const userManager = {
  ...commonManager('users'),
  setInvitedAdmin: (userId, publisherId, mode = 'add') => {
    return new Promise(async (resolve, reject) => {
      try {
        if (isEmpty(userId)) {
          throw new Error('Unable to invite admin by empty userId');
        }
        if (isEmpty(publisherId)) {
          throw new Error('Unable to invite admin by empty publisherId');
        }

        if (isEqual(mode, 'delete')) {
          await basicManager.update({
            [`publishers/${publisherId}/administrators/${userId}`]: null,
            [`users/${userId}/soundcasts_managed`]: null,
            [`users/${userId}/admin`]: null,
            [`users/${userId}/publisherID`]: null,
          });
        } else {
          // get the initial soundcasts of the publisher
          // and copy it to the co amin user node `soundcasts_managed`
          const soundcasts = await publisherManager.getProp(publisherId, 'soundcasts');
          await basicManager.update({
            [`publishers/${publisherId}/administrators/${userId}`]: true,
            [`users/${userId}/soundcasts_managed`]: soundcasts,
            [`users/${userId}/admin`]: true,
            [`users/${userId}/publisherID`]: publisherId,
          });
        }

        resolve(true);
      } catch (err) {
        console.error(
          `ERROR: userManager.setInvitedAdmin ${JSON.stringify({mode, userId, publisherId})} errored`,
          err.message,
        );
        reject(err);
      }
    });
  },
  deleteInvitedAdmin: (userId, publisherId) => {
    return userManager.setInvitedAdmin(userId, publisherId, 'delete');
  },
};

const invitationManager = {
  ...commonManager('invitations'),
  getActiveInvitations: (email) => {
    return new Promise(async resolve => {
      try {
        if (isEmpty(email)) {
          throw new Error(`The given email is empty`);
        }

        const invitationId = 
          (isArray(email) ? email[0] : email).replace(/\./g, '(dot)').toLowerCase();

        const snapshot = await firebase.database()
          .ref(`invitations/${invitationId}`)
          .orderByValue()
          .equalTo(true)
          .once('value');

        const result = snapshot.exists()
          ? _.chain(snapshot.val()).map((value, key) => key).value()
          : [];

        resolve(result);
      } catch (err) {
        console.error(`ERROR: invitation manager - getActiveInvitations errored`, err.message);
        resolve(null);
      }
    });
  },
};

const paymentManager = {
  getSouncastPayment: (email, soundcastId) => {
    return new Promise(async resolve => {
      try {
        if (isEmpty(email)) {
          throw new Error(`The given email is empty`);
        }
        if (isEmpty(soundcastId)) {
          throw new Error(`The given soundcast id is empty`);
        }

        const { data } = await Axios.get(`/api/paymentstate`, {
          params: {
            email,
            soundcastId,
          },
        });

        resolve(data);
      } catch (err) {
        console.error(`ERROR: payment manager - getSouncastPayment errored`, err.message);
        resolve(null);
      }
    });
  },
  removeSouncastPayment: (email, soundcastId) =>  {
    return new Promise(async resolve => {
      try {
        if (isEmpty(email)) {
          throw new Error(`The given email is empty`);
        }
        if (isEmpty(soundcastId)) {
          throw new Error(`The given soundcast id is empty`);
        }

        await Axios.delete(`/api/paymentstate`, {
          params: {
            email,
            soundcastId,
          },
        });

        resolve(true);
      } catch (err) {
        console.error(
          `ERROR: payment manager - removeSouncastPayment errored`,
          err.message,
          JSON.stringify({ email, soundcastId }),
        );
        resolve(false);
      }
    });
  },
  getSoundcastPaymentDelay: async (email, soundcastId, numberOfAttempts = 10, msDelay = 5000) => {
    const payState = await paymentManager.getSouncastPayment(email, soundcastId);
    
    if (!isEmpty(payState)) {
      return payState;
    }
    if (numberOfAttempts <= 0) {
      return null;
    }
    console.log(
      `WARNING: payment manager - getSoundcastPaymentDelay ${email} ${soundcastId}`,
      `retry (${numberOfAttempts}) to get after ${msDelay} ms...`,
    );
    await new Promise(resolve => setTimeout(resolve, msDelay));
    return await paymentManager.getSoundcastPaymentDelay(email, soundcastId, numberOfAttempts - 1);
  },
};

const shopifyManager = {
  ...commonManager('shopify'),
};

export {
  authManager,
  basicManager,
  couponsManager,
  courseManager,
  episodeManager,
  messageManager,
  publisherManager,
  soundcastManager,
  soundcastExtManager,
  userManager,
  invitationManager,
  paymentManager,
  shopifyManager,
};
